/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { retry, timeout } from 'rxjs/operators';
import { Constants } from '../../common/constants';
import { UserService } from '../services/user.service';
import { CloudRepo } from '../services/workflow.service';
import { MiddlewareBrowseModalComponent } from './middleware-browse-modal.component';
import { BrowserService } from '../services/browser.service';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';

@Injectable({
  providedIn: 'root',
})
export class MiddlewareService {
  private baseUrl = 'api/middleware/';
  private readonly LICENSE_REQUIRED = 'LICENSE_REQUIRED';

  private _connector = '';
  get connector(): string {
    return this._connector;
  }
  set connector(value: string) {
    this._connector = value;
    this.connectorDisplayName = this.getConnectorDisplayName(); // relies on region to be set before connector
  }

  public region = '';
  public connectorDisplayName = '';
  public connectorToken = '';
  public connectorRefreshToken = '';
  public connectorAccessListId = '';
  public repoOptions: any;
  public readonly licenseErrorClosed = new EventEmitter<void>();

  directoryOrFilesSelected: EventEmitter<any>;

  private lastLoginUrl = '';
  private loginWindow: Window | null = null;
  private pollingInterval: number | null = null;
  private enableFileTypes = '';
  private isMultipleSelection = false;
  private maxSelection = 0;

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private userService: UserService,
    private browerService: BrowserService,
  ) {
    this.directoryOrFilesSelected = new EventEmitter();
  }

  private async validateSharePointLicense(): Promise<void> {
    if (this.connector !== 'GraphSharePoint') {
      return;
    }

    if (!this.connectorToken && this.connectorRefreshToken) {
      await this.refreshToken();
    }

    if (!this.connectorToken) {
      return;
    }

    try {
      const hasRequiredLicense = await this.http
        .get<boolean>(`${this.baseUrl}GetM365LicenseDetails`, {
          params: {
            token: this.connectorToken,
            region: this.region,
            connector: this.connector,
          },
        })
        .toPromise();

      if (!hasRequiredLicense) {
        throw new Error(this.LICENSE_REQUIRED);
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 400) {
          throw new Error(this.LICENSE_REQUIRED);
        }        
        throw new Error('UNEXPECTED_FAILURE');
      }
      throw error;
    }
  }

  private async showLoginError(errorMessage: string): Promise<void> {
    this.loginWindow?.close();
    this.loginWindow = null;

    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title =
      errorMessage === this.LICENSE_REQUIRED
        ? 'SDE_ACCOUNT_NOT_FOUND'
        : 'SDE_SYSTEM_ENCOUNTERED_ERROR1';

    modalRef.componentInstance.body =
      errorMessage === this.LICENSE_REQUIRED
      ? 'SDE_SPECIFIED_ACCOUNT_DOESNT'
        : '';

    await modalRef.result.catch(() => undefined);

    // notify workflow modal to return to Output settings
    this.licenseErrorClosed.emit();
  }

  public async initiateWebLogin(
    repo: CloudRepo,
    returning = false,
    enableFileTypes = '',
    isMultipleSelection = false,
    maxSelection = 0,
  ) {
    let attempts = 0;
    this.region = repo.region || '';
    this.connector = repo.connector;
    this.enableFileTypes = enableFileTypes;
    this.isMultipleSelection = isMultipleSelection;
    this.maxSelection = maxSelection;

    while (attempts < 2) {
      attempts++;
      try {
        const loginUrl = await this.http
          .get<string>(`${this.baseUrl}GetWebLogin`, {
            params: {
              UserAgent: window.navigator.userAgent,
              connector: this.connector,
              region: this.region,
              accessListId: this.userService.user?.accessListId || '',
            },
          })
          .pipe(timeout(1000), retry(3))
          .toPromise();
        if (loginUrl) {
          if (!returning) {
            this.repoOptions = null;
          }
          return this.openLoginWindow(loginUrl);
        }
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
  }

  public async returningWebLogin(
    returnRepo: CloudRepo,
    enableFileTypes = '',
    isMultipleSelection = false,
  ) {
    let attempts = 0;
    this.region = returnRepo.region || '';
    this.connector = returnRepo.connector || '';
    this.connectorToken = returnRepo.access || '';
    this.connectorRefreshToken = returnRepo.refresh || '';
    this.enableFileTypes = enableFileTypes;
    this.isMultipleSelection = isMultipleSelection;

    while (attempts < 2) {
      attempts++;
      try {
        const loginUrl = await this.http
          .get<string>(`${this.baseUrl}GetWebLogin`, {
            params: {
              UserAgent: window.navigator.userAgent,
              connector: this.connector,
              region: this.region,
              accessListId: this.userService.user?.accessListId || '',
            },
          })
          .toPromise();
        if (loginUrl) {
          // use the returning dir and path for ValidatePath() call
          this.repoOptions = {
            dir: returnRepo.dir,
            sitePath: returnRepo.sitePath,
          };
          return this.openLoginWindow(loginUrl);
        }
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
  }
  // TODO remove when we remove MFD code
  // public async initiateLogin(repo: CloudRepo, returning: boolean = false) {
  //   let attempts = 0;
  //   this.region = repo.region || '';
  //   this.connector = repo.connector || '';
  //   //Lexmark support adjustments
  //   const userAgent = this.browerService.isLexmark
  //     ? 'X2G_'
  //     : window.navigator.userAgent;
  //   const model = this.browerService.isLexmark
  //     ? 'versalink'
  //     : this.eipApiService.deviceInfo.model;
  //   const softwareVersion = this.browerService.isLexmark
  //     ? '100.001.049.17300'
  //     : this.eipApiService.deviceInfo.firmwareVersion;

  //   while (attempts < 2) {
  //     attempts++;
  //     try {
  //       const loginUrl = await this.http
  //         .get<string>(`${this.baseUrl}GetLogin`, {
  //           params: {
  //             Model: encodeURIComponent(model),
  //             EipVersion: encodeURIComponent(
  //               this.eipApiService.deviceInfo.eipVersion,
  //             ),
  //             SoftwareVersion: encodeURIComponent(softwareVersion),
  //             UserAgent: encodeURIComponent(userAgent),
  //             connector: encodeURIComponent(this.connector),
  //             region: encodeURIComponent(this.region),
  //             accessListId: encodeURIComponent(
  //               this.userService.user.accessListId,
  //             ),
  //           },
  //         })
  //         .toPromise();
  //       if (loginUrl) {
  //         if (!returning) {
  //           this.repoOptions = null;
  //         }
  //         window.location.href = loginUrl;
  //       }
  //     } catch (error) {
  //       await this.refreshToken();
  //       if (attempts == 2) {
  //         console.log('Detected Error, Refreshing token did not correct');
  //         throw error;
  //       }
  //     }
  //   }
  // }

  //Throws exception if token is missing
  validateConnectorToken() {
    if (!this.connectorToken) {
      throw new Error('ACCESS_TOKEN_REQUIRED');
    }
  }

  //Throws exception if token is invalid, or folder does not exist
  async validatePath(repoOptions: CloudRepo): Promise<boolean> {
    let throwException = false;
    try {
      if (this.connector == 'DocuShare') {
        let dirId = repoOptions.dir?.id.endsWith('/')
          ? repoOptions.dir.id.slice(0, -1)
          : repoOptions.dir?.id;
        dirId = dirId?.split('/').pop(); // extract the last directory id if there's a path of ids
        const folderContents = await this.browseById('', '', dirId || '');
      } else {
        const dirContents = await this.browse(
          'ListDir',
          repoOptions.dir || null,
          repoOptions.sitePath ? repoOptions.sitePath[0] : null,
        );
      }
    } catch (e) {
      throwException = true;
    }
    if (throwException) {
      throw new Error('BROWSE_FOLDER_REQUIRED');
    } else return throwException;
  }

  public async browse(
    operation: string,
    dir: MWCloudItem | null,
    sitePath: MWCloudItem | null,
  ) {
    let result = '';
    let attempts = 0;
    this.validateConnectorToken();
    let dirName = dir ? dir.name : '';
    let sitePathName = sitePath ? sitePath.name : '';

    if (this.connector == 'DocuShare') {
      dirName = dir ? dir.id : '';
      sitePathName = sitePath ? sitePath.id : '';
    }

    while (attempts < 2) {
      attempts++;
      try {
        const browseItems = await this.http
          .get<string>(`${this.baseUrl}Browse`, {
            params: {
              token: this.connectorToken,
              connector: this.connector,
              region: this.region,
              op: operation,
              dir: dirName,
              sitePath: sitePathName,
            },
          })
          .toPromise();
        if (browseItems) {
          result = browseItems;
        }
        attempts = 2;
      } catch (error) {
        if ((error as { status: number; error: string }).status !== undefined) {
          const e = error as { status: number; error: string };
          if (e.error == 'NOT_FOUND') {
            throw error;
          }
        }
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
    return result;
  }

  public async browseById(siteId: string, libraryId: string, folderId: string) {
    let result: FolderResponse | null = null;
    let attempts = 0;
    this.validateConnectorToken();

    while (attempts < 2) {
      attempts++;
      try {
        const json = await this.http
          .get<FolderResponse>(`${this.baseUrl}BrowseById`, {
            params: {
              token: this.connectorToken,
              connector: this.connector,
              region: this.region,
              siteId: siteId || '',
              libraryId: libraryId || '',
              folderId: folderId || '',
            },
          })
          .toPromise();
        if (json) {
          result = json;
        }
        attempts = 2;
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
    return result;
  }

  public async getSites() {
    let result: SiteResponse | null = null;
    let attempts = 0;
    this.validateConnectorToken();

    while (attempts < 2) {
      attempts++;
      try {
        const json = await this.http
          .get<SiteResponse>(`${this.baseUrl}GetSites`, {
            params: {
              token: this.connectorToken,
              connector: this.connector,
              region: this.region,
            },
          })
          .toPromise();
        if (json) {
          result = json;
        }
        attempts = 2;
      } catch (error) {
        await this.refreshToken();
        if (attempts == 2) {
          console.log('Detected Error, Refreshing token did not correct');
          throw error;
        }
      }
    }
    return result;
  }

  private getConnectorDisplayName(): string {
    let name = '';
    const midDest = Constants.middlewareDestinations.find(
      (x) => x.value === this.connector && x.region == this.region,
    );
    name = (midDest && midDest.key) || this.connector;
    return name;
  }

  private async refreshToken() {
    if (this.connectorRefreshToken) {
      try {
        const tokens = await this.http
          .get<string>(`${this.baseUrl}RefreshToken`, {
            params: {
              token: this.connectorRefreshToken,
              connector: this.connector,
              region: this.region,
              accessListId: this.userService.user?.accessListId || '',
            },
          })
          .toPromise();
        if (tokens) {
          const refreshedTokens = JSON.parse(JSON.stringify(tokens));
          this.connectorToken = refreshedTokens.accessToken;
          this.connectorRefreshToken = refreshedTokens.refreshToken;
        }
      } catch (error) {
        throw new Error('ACCESS_TOKEN_REQUIRED');
      }
    }
  }

  private openLoginWindow(url: string) {
    // remove any left over listener
    window.removeEventListener('message', this.receiveLoginMessage, false);

    const height = 700;
    const width = 600;
    const left = screen.width / 2 - width / 2;
    const top = screen.height / 2 - height / 2;
    const screenX = window.screenX || 0;

    // window features
    const features = `toolbar=no, menubar=no, width=${width}, height=${height}, top=${top}, left=${left + screenX}`;

    // If new window just open
    if (this.loginWindow == null || this.loginWindow.closed) {
      this.loginWindow = window.open(url, 'cloud login', features);

      // If window changed, open and focus
    } else if (this.lastLoginUrl !== url) {
      this.loginWindow = window.open(url, 'cloud login', features);
      this.loginWindow?.focus();
    } else {
      // else just focus
      this.loginWindow?.focus();
    }

    // add listener for receiving tokens
    window.addEventListener('message', this.receiveLoginMessage, false);
    if (this.connector == 'Dropbox') {
      this.pollForLoginWindowClose();
    }

    // retain last used login url
    this.lastLoginUrl = url;
  }

  private pollForLoginWindowClose() {
    if (this.pollingInterval == null) {
      this.pollingInterval = window.setInterval(() => {
        if (this.loginWindow?.closed) {
          const mwData = localStorage.getItem('mwData');
          if (mwData != null) {
            this.receiveLoginMessage(JSON.parse(mwData));
            clearInterval(this.pollingInterval as number);
            this.pollingInterval = null;
          }
          localStorage.removeItem('mwData');
        }
      }, 2000);

      window.setTimeout(() => {
        clearInterval(this.pollingInterval as number);
        this.pollingInterval = null;
      }, 300000);
    }
  }
  private receiveLoginMessage = async (message: MessageEvent) => {
    if (
      message &&
      message.data &&
      message.data.connectorProvider === 'middleware'
    ) {
      window.removeEventListener('message', this.receiveLoginMessage, false);
      this.region = message.data.region;
      this.connector = message.data.connector;
      this.connectorToken = message.data.access;
      this.connectorRefreshToken = message.data.refresh;

      try {
        await this.validateSharePointLicense();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : null;
        if (errorMessage) {
          await this.showLoginError(errorMessage);
        }
        return;
      }

      if (!this.repoOptions) {
        this.openBrowseModal(false);
      } else {
        try {
          await this.validatePath(this.repoOptions);
          this.repoOptions.connector = this.connector;
          this.repoOptions.region = this.region;
          this.repoOptions.access = this.connectorToken;
          this.repoOptions.refresh = this.connectorRefreshToken;
          this.directoryOrFilesSelected.emit(this.repoOptions);
        } catch {
          this.openBrowseModal(true);
        }
      }
    }
  };

  private openBrowseModal(returning: boolean) {
    const modalRef = this.modalService.open(MiddlewareBrowseModalComponent, {
      windowClass: 'xrx-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.enableFileTypes = this.enableFileTypes;
    modalRef.componentInstance.multiSelect = this.isMultipleSelection;
    modalRef.componentInstance.maxSelection = this.maxSelection;
    modalRef.componentInstance.returning = returning;

    modalRef.result
      .then((repoOptions) => {
        this.repoOptions = repoOptions;
        this.directoryOrFilesSelected.emit(repoOptions);
      })
      .catch((repoOptions) => {
        this.directoryOrFilesSelected.emit(repoOptions);
      });
  }

}

export interface FolderResponse {
  pageToken?: string;
  folders?: MWItem[];
  files?: MWItem[];
}

export interface SiteResponse {
  nextPage?: string;
  sites?: MWItem[];
}

export interface MWItem {
  name: string;
  id?: string;
  Path: string;
  libraryId?: string;
  folderId?: boolean;
  isCollection?: boolean;
  readOnly?: boolean;
}

export interface MWCloudItem {
  name: string;
  id: string;
}
